import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, BookMarked, Trash2, Eye, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/CategoryBadge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DesktopHint } from "@/components/studio/DesktopOnly";
import { FeedbackButton } from "@/components/studio/FeedbackButton";
import { useIsMobile } from "@/lib/useIsMobile";
import { SceneLibrary } from "@/components/studio/SceneLibrary";
import { WritingStats } from "@/components/studio/WritingStats";
import { api, errMsg } from "@/lib/api";
import { fmtNum, visibilityLabel } from "@/lib/studio";

const NewStoryDialog = ({ uid, onClose, onCreated }) => {
  const [title, setTitle] = useState("");
  const [synopsis, setSynopsis] = useState("");
  const create = async () => {
    try { onCreated((await api.post(`/universes/${uid}/stories`, { title, synopsis })).data); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-story-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Start a story</DialogTitle><DialogDescription>It begins as a private Draft 1. You choose later when your team can read it.</DialogDescription></DialogHeader>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Story title" data-testid="new-story-title" />
        <Textarea value={synopsis} onChange={(e) => setSynopsis(e.target.value)} placeholder="One-line synopsis (optional)" data-testid="new-story-synopsis" />
        <DialogFooter><Button onClick={create} disabled={!title.trim()} data-testid="new-story-create">Create story</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StoryCard = ({ s, base, isOwner, onDelete }) => (
  <Link to={`${base}/studio/${s.id}`} className="group relative block rounded-md border border-[#2D3442] bg-[#13171F] p-6 transition-colors hover:border-[#D97706]/60" data-testid={`story-card-${s.id}`}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-[#A8A090]">
      {s.visibility === "private" ? <Lock className="h-3 w-3" /> : <Eye className="h-3 w-3 text-[#10B981]" />}<span data-testid={`story-visibility-${s.id}`}>{visibilityLabel(s)}</span>
    </div>
    <div className="mt-3 font-display text-3xl text-[#F3EBDD] group-hover:text-[#F59E0B] [overflow-wrap:anywhere]">{s.title}</div>
    {s.synopsis && <p className="mt-2 line-clamp-2 text-sm text-[#A8A090]">{s.synopsis}</p>}
    <div className="mt-5 flex gap-5 text-xs text-[#A8A090]" data-testid={`story-stats-${s.id}`}>
      <span>{fmtNum(s.words)} words</span><span>{s.chapters} chapters</span><span>{s.drafts} draft{s.drafts === 1 ? "" : "s"}</span>
    </div>
    {isOwner && <button onClick={(e) => { e.preventDefault(); onDelete(s); }} className="absolute right-4 top-4 text-[#A8A090] opacity-0 hover:text-[#F87171] group-hover:opacity-100" data-testid={`story-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>}
  </Link>
);

export default function StudioPage() {
  const { universe, base } = useOutletContext();
  const uid = universe.id;
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ["stories", uid], queryFn: () => api.get(`/universes/${uid}/stories`).then((r) => r.data), retry: false });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const isOwner = universe.role === "owner";
  const refresh = () => qc.invalidateQueries({ queryKey: ["stories", uid] });
  const stories = data?.stories || [];
  const mobile = useIsMobile();
  return (
    <>
      <div className="mx-auto max-w-6xl px-4 py-8 md:px-10 md:py-12" data-testid="studio-page">
        <PageHeader eyebrow="Writing Studio" title="Stories">
          {isOwner && <Button onClick={() => setCreating(true)} data-testid="new-story-button"><Plus className="mr-1 h-4 w-4" />New story</Button>}
        </PageHeader>
        {error && <p className="text-sm text-[#F87171]" data-testid="studio-error">{errMsg(error)}</p>}
        {isOwner && data?.tier === "free" && <p className="mb-6 text-xs text-[#A8A090]" data-testid="studio-free-note">Free plan: one story with one draft. <Link to="/pricing" className="text-[#F59E0B] hover:underline">Pro</Link> adds more stories and drafts.</p>}
        <Tabs defaultValue="stories">
          {isOwner && !mobile && <TabsList><TabsTrigger value="stories" data-testid="studio-tab-stories">Stories</TabsTrigger><TabsTrigger value="scenes" data-testid="studio-tab-scenes">Scene notes</TabsTrigger><TabsTrigger value="stats" data-testid="studio-tab-stats">Writing stats</TabsTrigger></TabsList>}
          <TabsContent value="stories" className="mt-6">
            {data && stories.length === 0 && (
              <div className="rounded-md border border-dashed border-[#2D3442] p-14 text-center" data-testid="stories-empty">
                <BookMarked className="mx-auto h-8 w-8 text-[#A8A090]" />
                <p className="mt-3 font-display text-2xl text-[#F3EBDD]">{isOwner ? "Your first story starts here." : "No stories have been shared yet."}</p>
              </div>
            )}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3" data-testid="stories-grid">
              {stories.map((s) => <StoryCard key={s.id} s={s} base={base} isOwner={isOwner} onDelete={setDeleting} />)}
            </div>
            {mobile && isOwner && <div className="mt-6"><DesktopHint>Scene notes, writing stats and the widget panel open on desktop.</DesktopHint></div>}
          </TabsContent>
          {isOwner && !mobile && <TabsContent value="scenes" className="mt-6"><SceneLibrary uid={uid} /></TabsContent>}
          {isOwner && !mobile && <TabsContent value="stats" className="mt-6"><WritingStats /></TabsContent>}
        </Tabs>
      </div>
      {creating && <NewStoryDialog uid={uid} onClose={() => setCreating(false)} onCreated={() => { toast.success("Story created"); refresh(); }} />}
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title={`Delete "${deleting?.title}"?`} description="Every draft and chapter of this story is deleted for good. Scene notes stay in your library."
        onConfirm={async () => { await api.delete(`/stories/${deleting.id}`); toast.success("Story deleted"); setDeleting(null); refresh(); }} />
      {!mobile && <FeedbackButton />}
    </>
  );
}
